import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { round } from 'lodash';
import { Object3D, Plane, Raycaster, Vector2, Vector3 } from 'three';
import { ShapeState2D } from '../../cross-team-shared/annotation/shared/state/shape-state';
import { isVideoOutput } from '../../cross-team-shared/misc-utils/output-utils';
import { ProjectionType } from '../../cross-team-shared/misc-utils/projection-utils';
import {
  Point2D,
  Point3D,
} from '../../team-apis/otterdam/answers/shape-typings';
import { SingleFrameCalibrationData } from '../../team-apis/otterdam/calibration-data';
import {
  ProjectionCamera,
  updateProjectionCamera,
} from '../renderers/projection/projection-camera';
import { CuboidState3D } from '../state/cuboid-state-3d';
import { X, Y } from '../state/point-cloud-content-state';
import { PointCloudContentStore } from '../state/point-cloud-content-store';
import { getCameraData } from '../utils/calibration-utils';
import { KdTreeService } from '../utils/kd-tree.service';
import {
  eulerToPoint,
  getLegalizedScale,
  vectorToPoint,
} from '../utils/point-cloud-utils';
import { subtractPoints } from '../utils/point-utils';
import { projectionUtils } from '../utils/projections/projection-utils';
import { CalibrationService } from './calibration.service';

const defaultDistance = 5;
const projectedCuboidLength = 4.5;

@Injectable()
export class UnprojectionService extends InjectableSuperclass {
  private kdTreeService = inject(KdTreeService);
  private store = inject(PointCloudContentStore);
  private calibrationService = inject(CalibrationService);

  private raycaster = new Raycaster();
  private guidingProjectionCamera = new ProjectionCamera();

  private cdiLutProjectionUtils = projectionUtils.lut;

  buildProjectedCuboid(
    videoOutputName: string,
    points: Point2D[],
    originShape: ShapeState2D,
  ): CuboidState3D {
    assert(
      !this.hasMultiplePixelToLightrayLut(),
      'Only one pixelToLightray LUT is supported for unprojections',
    );

    const { position, rotation, scale } = this.getCuboidLocation(
      points,
      videoOutputName,
    );

    return {
      ...originShape,
      type: 'cuboid',
      key_locations: [
        {
          frame_number: this.store.state().render.frameNumber,
          position,
          rotation,
          scale,
          points: [],
          visibility: 1,
        },
      ],
    };
  }

  private hasMultiplePixelToLightrayLut() {
    const { outputs } = this.store.state().project;
    const outputsProjectionType = outputs
      .filter((output) => isVideoOutput(output))
      .map((output) => this.calibrationService.getProjectionType(output.name));

    const lutRequiredProjectionTypes: ProjectionType[] = [
      'deformed_cylindrical',
      'cylinder',
      'lut',
    ];
    const projectionsRequiringLut = outputsProjectionType.filter(
      (projectionType) => lutRequiredProjectionTypes.includes(projectionType),
    );

    return projectionsRequiringLut.length > 1;
  }

  private getCuboidLocation(points: Point2D[], videoOutputName: string) {
    const calibration = getCameraData(
      this.store.state().calibrationData[videoOutputName],
      this.store.state().render.calibrationOptions[videoOutputName],
      this.store.state().render.frameNumber,
    );

    try {
      const intersection = this.getCloudIntersection(points, videoOutputName);

      const position = vectorToPoint(intersection);
      const rotation = getProjectionRotation(intersection, calibration);
      const scale = this.getCuboidScale(points, videoOutputName, intersection);

      return { position, rotation, scale };
    } catch (error: unknown) {
      const bounds = getMinMax(points);
      const intersection = this.getDefaultIntersection(bounds, videoOutputName);

      const position = vectorToPoint(intersection);
      const rotation = getProjectionRotation(intersection, calibration);
      const scale = this.getDefaultScale(bounds, videoOutputName);

      return { position, rotation, scale };
    }
  }

  private getCloudIntersection(points: Point2D[], videoOutputName: string) {
    const { min, max } = getMinMax(points);
    const shapeCenter: Point2D = [
      round(min[X] + (max[X] - min[X]) / 2),
      round(min[Y] + (max[Y] - min[Y]) / 2),
    ];
    const ray = this.getRayFromPoint(shapeCenter, videoOutputName);
    return this.kdTreeService.getClosestPoint(ray);
  }

  private getCuboidScale(
    points: Point2D[],
    videoOutputName: string,
    cloudPoint: Vector3,
  ): Point3D {
    const { min, max } = getMinMax(points);
    const { left, right, top } = this.getPlaneIntersections(
      { min, max },
      videoOutputName,
      cloudPoint,
    );

    const width = new Vector3().subVectors(right, left).length();
    const height = new Vector3().subVectors(top, left).length();

    return getLegalizedScale([width, projectedCuboidLength, height]);
  }

  private getPlaneIntersections(
    { min, max }: { min: Point2D; max: Point2D },
    videoOutputName: string,
    cloudPoint: Vector3,
  ) {
    const { position } = getCameraData(
      this.store.state().calibrationData[videoOutputName],
      this.store.state().render.calibrationOptions[videoOutputName],
      this.store.state().render.frameNumber,
    );
    const pointToCamera = subtractPoints(vectorToPoint(cloudPoint), position);

    const plane = new Plane().setFromNormalAndCoplanarPoint(
      new Vector3().fromArray(pointToCamera).normalize(),
      cloudPoint,
    );

    const left = this.getRayFromPoint(
      [min[X], min[Y]],
      videoOutputName,
    ).intersectPlane(plane, new Vector3());
    const right = this.getRayFromPoint(
      [max[X], min[Y]],
      videoOutputName,
    ).intersectPlane(plane, new Vector3());
    const top = this.getRayFromPoint(
      [min[X], max[Y]],
      videoOutputName,
    ).intersectPlane(plane, new Vector3());

    assert(right && left && top, 'Ray-Plane intersection not found');

    return { left, right, top };
  }

  private getDefaultIntersection(
    { min, max }: { min: Point2D; max: Point2D },
    videoOutputName: string,
  ) {
    const shapeCenter: Point2D = [
      min[X] + (max[X] - min[X]) / 2,
      min[Y] + (max[Y] - min[Y]) / 2,
    ];
    const ray = this.getRayFromPoint(shapeCenter, videoOutputName);
    return ray.at(defaultDistance + projectedCuboidLength / 2, new Vector3());
  }

  private getDefaultScale(
    { min, max }: { min: Point2D; max: Point2D },
    videoOutputName: string,
  ): Point3D {
    const bottomLeft = this.getRayFromPoint(
      [min[X], min[Y]],
      videoOutputName,
    ).at(defaultDistance, new Vector3());
    const bottomRight = this.getRayFromPoint(
      [max[X], min[Y]],
      videoOutputName,
    ).at(defaultDistance, new Vector3());
    const topLeft = this.getRayFromPoint([min[X], max[Y]], videoOutputName).at(
      defaultDistance,
      new Vector3(),
    );

    const width = new Vector3().subVectors(bottomRight, bottomLeft).length();
    const height = new Vector3().subVectors(bottomLeft, topLeft).length();

    return getLegalizedScale([width, projectedCuboidLength, height]);
  }

  private getRayFromPoint(point: Point2D, videoOutputName: string) {
    const state = this.store.state();

    if (isDefined(state.render.lutData?.pixelToLightray)) {
      // @ts-ignore -- fix me
      const ray = this.cdiLutProjectionUtils.projectTo3D(
        new Vector2().fromArray(point),
        state,
        videoOutputName,
      );
      this.raycaster.ray.copy(ray);
    } else {
      const imageSize = state.projectionImageSize[videoOutputName];
      const { render, calibrationData } = this.store.state();
      updateProjectionCamera(
        this.guidingProjectionCamera,
        getCameraData(
          calibrationData[videoOutputName],
          this.store.state().render.calibrationOptions[videoOutputName],
          render.frameNumber,
        ),
        // @ts-ignore -- fix me
        imageSize,
      );
      this.raycaster.setFromCamera(
        // @ts-ignore -- fix me
        new Vector2(...getNdcPoint(point, imageSize)),
        this.guidingProjectionCamera,
      );
    }

    return this.raycaster.ray;
  }
}

function getProjectionRotation(
  cloudPoint: Vector3,
  { position, rotation }: SingleFrameCalibrationData,
) {
  const guide = new Object3D();
  guide.position.fromArray(position);
  guide.quaternion.fromArray(rotation);
  guide.up.set(0, 0, 1);
  guide.lookAt(cloudPoint);
  guide.rotateX(-Math.PI / 2);
  return eulerToPoint(guide.rotation);
}

function getMinMax(points: Point2D[]) {
  const min: Point2D = [
    Math.min(...points.map((p) => p[0])),
    Math.min(...points.map((p) => p[1])),
  ];
  const max: Point2D = [
    Math.max(...points.map((p) => p[0])),
    Math.max(...points.map((p) => p[1])),
  ];
  return { min, max };
}

// normalized device coordinates
function getNdcPoint(point: Point2D, imageSize: Point2D): Point2D {
  return [
    (point[0] / imageSize[0]) * 2 - 1,
    ((imageSize[1] - point[1]) / imageSize[1]) * 2 - 1,
  ];
}
